import { action, computed, observable } from 'mobx';
import moment from 'moment';
import { AccelFile, Scenario } from '../..';
import { Lang } from '../../../enums';
import { isEmpty } from '../../../utils';
import { Designable } from '../../Entity';
import CabinetComponent from '../component/cabinet-component';

export enum CabinetPageLayoutType {
    Default = 'default',
    Custom = 'custom'
}

export type CabinetPageLang = Lang | 'custom' | null;

export default class CabinetPage extends Designable {
    constructor(item?: Partial<CabinetPage>) {
        super(item);
        if (item) this.update(item);
    }

    @observable cabinetId: string;
    @observable name: string;
    @observable internalName: string;
    @observable segment: string;
    @observable url: string;
    @observable shortDescription: string;
    @observable icon: string | null = null;
    @observable isPublished: boolean;
    @observable isProtected: boolean;
    @observable isVisible: boolean = true;
    @observable canToggleVisibility: boolean = false;
    @observable parentId: string | null;
    @observable position: number;

    @observable image: AccelFile | null;

    @observable lang: CabinetPageLang;
    @observable customLang: string | null;

    @observable headHtml: string;
    @observable bodyHtml: string;

    @observable layoutType: CabinetPageLayoutType;

    @observable createdDate: moment.Moment;
    @observable updatedDate: moment.Moment | null;

    @observable parent?: CabinetPage | null;
    @observable header?: CabinetComponent | null;
    @observable footer?: CabinetComponent | null;
    @observable scenario?: Scenario;

    @computed get hasInternalName() {
        return !isEmpty(this.internalName);
    }

    static fromJson(json: any): CabinetPage {
        const item = new CabinetPage({
            ...json,
            createdDate: json.createdDate ? moment(json.createdDate) : undefined,
            updatedDate: json.updatedDate ? moment(json.updatedDate) : null,
            parent: json.parent ? CabinetPage.fromJson(json.parent) : undefined,
            image: json.image ? AccelFile.fromJson(json.image) : null,
            header: json.header ? CabinetComponent.fromJson(json.header) : null,
            footer: json.footer ? CabinetComponent.fromJson(json.footer) : null,
            lang: !isEmpty(json.customLang) ? 'custom' : json.lang ?? null,
            customLang: json.customLang ?? null,
            scenario: json.scenario ? Scenario.fromJson(json.scenario) : undefined,
        });
        if (item.scenario) item.scenario.update({ cabinetPage: item });
        return item;
    }

    @action
    update(changes: Partial<CabinetPage>, allowUndefined: boolean = false) {
        super.update(changes, allowUndefined);
    }

    clone(changes?: Partial<CabinetPage>) {
        return new CabinetPage({ ...this, ...changes });
    }

    hasChanges(page: CabinetPage) {
        return this.name != page.name
            || this.internalName != page.internalName
            || this.shortDescription != page.shortDescription
            || this.segment != page.segment
            || this.header?.id != page.header?.id
            || this.footer?.id != page.footer?.id
            || this.layoutType != page.layoutType
            || this.image?.id != page.image?.id
            || this.lang != page.lang
            || this.customLang != page.customLang
            || this.isVisible != page.isVisible;
    }
}
